import { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND, VEHICLES } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitEnquiry } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    vehicle_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please fill in name and phone");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry({ ...form, source: "contact-page" });
      toast.success("Thanks! Our team will call you shortly.");
      setForm({ name: "", phone: "", email: "", city: "", vehicle_interest: "", message: "" });
    } catch {
      toast.error("Something went wrong. WhatsApp us for a faster response.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] radial-glow-red pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-red-500 mb-4 font-mono-brand">
            <span className="w-8 h-px bg-current" /> Contact Us
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-white">
            Let's get you on the road
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-3xl leading-relaxed">
            Send us an enquiry, call us or WhatsApp — a Be Easy Cars expert will be with you within business hours.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Contact form */}
          <div className="lg:col-span-3 glass rounded-3xl p-8 md:p-10">
            <h3 className="font-display text-2xl font-bold text-white mb-2">Send us an enquiry</h3>
            <p className="text-slate-400 text-sm mb-8">All fields marked * are required.</p>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-300">Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Full name"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
                    data-testid="contact-input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-300">Phone *</Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="10-digit mobile"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
                    data-testid="contact-input-phone"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@email.com"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
                    data-testid="contact-input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-300">City</Label>
                  <Input
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    placeholder="Thane / Mumbai / …"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
                    data-testid="contact-input-city"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-300">Vehicle Interest</Label>
                <Select value={form.vehicle_interest} onValueChange={(v) => update("vehicle_interest", v)}>
                  <SelectTrigger
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="contact-select-vehicle"
                  >
                    <SelectValue placeholder="Choose a model" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1229] border-white/10 text-white">
                    {VEHICLES.map((v) => (
                      <SelectItem key={v.slug} value={v.name}>
                        {v.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="Not sure — need advice">Not sure — need advice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-300">Message</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={4}
                  placeholder="Share your requirement — budget, route, timeline…"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
                  data-testid="contact-input-message"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full h-12"
                data-testid="contact-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Enquiry
              </Button>
            </form>
          </div>

          {/* Info + Map */}
          <div className="lg:col-span-2 space-y-6">
            <div className="glass rounded-3xl p-8 space-y-5">
              <InfoRow icon={MapPin} label="Office" value={BRAND.address} />
              <InfoRow
                icon={Phone}
                label="Call"
                value={<a href={`tel:+91${BRAND.phone}`} className="hover:text-white transition-colors" data-testid="contact-phone-link">{BRAND.phoneDisplay}</a>}
              />
              <InfoRow
                icon={Mail}
                label="Email"
                value={<a href={`mailto:${BRAND.email}`} className="hover:text-white transition-colors break-all" data-testid="contact-email-link">{BRAND.email}</a>}
              />
              <InfoRow icon={Clock} label="Hours" value={BRAND.hours} />

              <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full">
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Us
                </Button>
              </a>
            </div>

            <div className="rounded-3xl overflow-hidden aspect-[4/3] border border-white/10">
              <iframe
                src="https://www.google.com/maps?q=Thane%20West%2C%20Maharashtra&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Be Easy Cars — Thane West"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-500/30 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-red-400" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-brand">{label}</div>
        <div className="text-white text-sm mt-1">{value}</div>
      </div>
    </div>
  );
}
